/**
 * Cross-method account linking (Cognito Pre-SignUp trigger) helpers.
 *
 * When a federated identity (Google / Microsoft) signs in for the FIRST time,
 * the Pre-SignUp Lambda links it to the native Cognito user with the same
 * verified email and aborts the in-flight attempt. Cognito surfaces that as
 * an OAuth error whose description contains one of the markers below.
 * Retrying the same authorize redirect ONCE then succeeds silently.
 */

/** Marker thrown by the Pre-SignUp Lambda after linking. */
export const ACCOUNT_LINKING_RETRY_MARKERS = [
  'ACCOUNT_LINKED_RETRY',
  // Cognito's own duplicate-entry error for a just-linked federated identity.
  'Already found an entry for username',
] as const

/** sessionStorage key prefix for the one-retry loop guard (per provider). */
export const ACCOUNT_LINK_RETRY_STORAGE_PREFIX = 'accountLinkRetry:'

/** True when an OAuth error/description indicates a just-linked account. */
export function isAccountLinkingRetryError(
  message: string | null | undefined,
): boolean {
  if (!message) return false
  return ACCOUNT_LINKING_RETRY_MARKERS.some(marker => message.includes(marker))
}

/** Release all account-link retry loop guards (call after a successful sign-in). */
export function clearAccountLinkRetryFlags(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (key?.startsWith(ACCOUNT_LINK_RETRY_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key)
      }
    }
  } catch {
    // sessionStorage unavailable — nothing to clear
  }
}
